"use client"

import { useEffect, useRef, useState } from "react"

declare global {
  interface Window {
    turnstile?: {
      render: (el: HTMLElement | string, options: any) => string
      reset: (id: string) => void
      remove: (id: string) => void
    }
    onTurnstileLoad?: () => void
  }
}

const SCRIPT_URL = "https://challenges.cloudflare.com/turnstile/v0/api.js?onload=onTurnstileLoad&render=explicit"

interface Props {
  onToken: (token: string | null) => void
  className?: string
}

/**
 * Cloudflare Turnstile widget. Env-gated by NEXT_PUBLIC_TURNSTILE_SITE_KEY.
 * When the site key is empty, this component renders nothing and immediately
 * calls onToken("") so forms keep working in dev.
 */
export function TurnstileWidget({ onToken, className }: Props) {
  const ref = useRef<HTMLDivElement>(null)
  const widgetIdRef = useRef<string | null>(null)
  const [scriptReady, setScriptReady] = useState(false)
  const siteKey = process.env.NEXT_PUBLIC_TURNSTILE_SITE_KEY || ""

  // No-op when not configured.
  useEffect(() => {
    if (!siteKey) {
      onToken(null)
      return
    }

    // Inject script once
    if (typeof window === "undefined") return
    if (window.turnstile) {
      setScriptReady(true)
      return
    }
    if (document.querySelector(`script[src^="https://challenges.cloudflare.com/turnstile"]`)) {
      // Wait for it to load
      const wait = setInterval(() => {
        if (window.turnstile) { setScriptReady(true); clearInterval(wait) }
      }, 100)
      return () => clearInterval(wait)
    }
    window.onTurnstileLoad = () => setScriptReady(true)
    const s = document.createElement("script")
    s.src = SCRIPT_URL
    s.async = true; s.defer = true
    document.head.appendChild(s)
  }, [siteKey, onToken])

  useEffect(() => {
    if (!siteKey || !scriptReady || !ref.current || !window.turnstile) return
    if (widgetIdRef.current) return       // already rendered

    widgetIdRef.current = window.turnstile.render(ref.current, {
      sitekey: siteKey,
      callback: (token: string) => onToken(token),
      "error-callback": () => onToken(null),
      "expired-callback": () => onToken(null),
      theme: "auto",
    })

    return () => {
      try {
        if (widgetIdRef.current && window.turnstile) {
          window.turnstile.remove(widgetIdRef.current)
        }
      } catch {}
      widgetIdRef.current = null
    }
  }, [scriptReady, siteKey, onToken])

  if (!siteKey) return null
  return <div ref={ref} className={className} />
}

export function isTurnstileConfigured(): boolean {
  return !!process.env.NEXT_PUBLIC_TURNSTILE_SITE_KEY
}
